import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  UserFeedback,
  UserFeedbackFeedbackType,
} from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Phone } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLocation } from "@tanstack/react-router";
import { useNewFeedback } from "@/requests/gen/react-query/feedback";
import { parseAsBoolean, useQueryState } from "nuqs";

interface FeedbackFormProps {
  trigger: React.ReactNode;
  isBugReport?: boolean;
  title?: string;
  message?: string;
}

export function FeedbackForm({
  trigger,
  isBugReport = false,
  title,
  message,
}: FeedbackFormProps) {
  const location = useLocation();
  const newFeedbackMutation = useNewFeedback();
  const [isOpen, setIsOpen] = useQueryState(
    "feedbackFormOpen",
    parseAsBoolean.withDefault(false),
  );

  const form = useForm<UserFeedback>({
    defaultValues: {
      feedback_type: isBugReport ? UserFeedbackFeedbackType.bug : undefined,
      subject: title || "",
      message: message || "",
      url: location.pathname + location.searchStr,
      image: null,
    },
  });

  // Update URL when location changes
  useEffect(() => {
    form.setValue("url", location.pathname + location.searchStr);
  }, [location.pathname, location.searchStr, form]);

  const onSubmit = (data: UserFeedback) => {
    newFeedbackMutation.mutate(
      { data },
      {
        onSuccess: () => {
          form.reset();
          setIsOpen(false);
          toast.success("Feedback submitted successfully!");
        },
        onError: (error) => {
          console.error("Failed to submit feedback:", error);
          toast.error(
            "Failed to submit feedback, please contact: support@legrandehealth.com",
          );
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Feedback Form</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">
            Submit feedback that will help us improve the portal.
          </DialogDescription>
          <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>Or call us at:</span>
            <a
              href="tel:+18554981272"
              className="font-medium text-primary hover:underline"
            >
              +1 (855) 498-1272
            </a>
          </div>
        </DialogHeader>
        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="feedback_type"
                rules={{ required: "Please select a feedback type" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What can we help with?</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ""}
                      disabled={isBugReport}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select feedback type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={UserFeedbackFeedbackType.practice}>
                          Practice Success{" "}
                          <span className="text-xs">
                            (Practice 360, Fulfillment 360, Warehouse 360)
                          </span>
                        </SelectItem>
                        <SelectItem value={UserFeedbackFeedbackType.support}>
                          General Support{" "}
                          <span className="text-xs">(Patient 360)</span>
                        </SelectItem>
                        <SelectItem value={UserFeedbackFeedbackType.bug}>
                          Bug Report
                        </SelectItem>
                        <SelectItem value={UserFeedbackFeedbackType.feature}>
                          Feature Request
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                rules={{
                  required: "Subject is required",
                  minLength: {
                    value: 1,
                    message: "Subject cannot be empty",
                  },
                  maxLength: {
                    value: 100,
                    message: "Subject must be 100 characters or less",
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subject</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter the subject of your help request"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                rules={{
                  required: "Message is required",
                  minLength: {
                    value: 1,
                    message: "Message cannot be empty",
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe your issue or question in detail"
                        className="min-h-24"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={undefined}
                        type="file"
                        accept="image/*"
                        multiple={false}
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            const file = e.target.files[0];

                            // Check file size (10MB limit)
                            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                            if (file.size > maxSize) {
                              toast.error("File size must be less than 10MB");
                              e.target.value = ""; // Clear the input
                              return;
                            }

                            // Convert file to base64 string
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              const base64String = reader.result as string;
                              form.setValue("image", base64String);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end gap-2 pt-4">
                <DialogClose asChild>
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  disabled={
                    newFeedbackMutation.isPending || !form.formState.isValid
                  }
                >
                  {newFeedbackMutation.isPending ? "Sending..." : "Send"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
